registerComponent("sections/submenu", function (component) {
/**
 * SUB MENU — rolagem horizontal do trilho de produtos e o estado PRESO.
 *
 * Abaixo do degrau as seis abas não cabem e o trilho passa a rolar (a regra é do
 * componente, em _submenu.less). Este script cuida de três coisas:
 *
 *   · dizer AO USUÁRIO que há mais conteúdo de cada lado — escreve
 *     `data-antes` / `data-depois` no envoltório, de onde saem tanto o
 *     esmaecimento da borda quanto a seta correspondente; avança o trilho ~70%
 *     da faixa visível a cada clique na seta; e mantém a aba atual à vista
 *     quando ela muda;
 *
 *   · avisar que a barra PRENDEU no topo (`submenu--fixo`), que é quando ela
 *     ganha o monograma e o CTA — ver `.submenu--fixo` em _submenu.less — e, na
 *     mesma passada, publicar em `--barra-solta` o quanto ela já largou da
 *     régua, que é o que mantém o gradiente da tampa ancorado na janela quando
 *     a barra sobe no fim da pilha;
 *
 *   · nada mais. A rolagem até a seção e o destaque da atual são do componente
 *     pai (sections/produtos), que é quem conhece as posições da pilha.
 *
 * Por que `data-*` e não classes nos dois primeiros estados: são estados
 * independentes de um mesmo componente, e o CSS os lê como seletor de atributo
 * sem inventar nomes.
 *
 * @param {HTMLElement} component - elemento raiz do componente (o <nav class="submenu">)
 * @param {Object} params
 */

const FOLGA = 2;      /* tolerância de subpixel nas pontas do trilho */
const AVANCO = 0.7;   /* fração da faixa visível por clique na seta */

const nav = component.querySelector('.submenu__nav');
const rail = nav && nav.querySelector('.submenu__rail');

/* ── A LINHA DA BARRA DO CELULAR PRECISA SABER A LARGURA DO CTA (18/09) ─────
   Presa, a barra do celular encosta o "Abrir conta" na direita e o chevron do
   seletor desliza para a esquerda para abrir lugar (`.submenu--fixo` no
   `@maxMd` de _submenu.less). O vão é 20, mas a LARGURA do botão não é
   constante: 96 com "ABRIR CONTA" e mais com "OPEN AN ACCOUNT" — a medida do
   desenho é o MÍNIMO, e o rótulo em inglês cresce a partir dele (ver `.btn--w*`
   em _button.less). Com o 96 fixo o chevron parava por baixo do botão em
   inglês. O CSS tem o 96 como padrão; esta medida o substitui.

   `offsetWidth`, e não `getBoundingClientRect`: o grupo entra em `scale` 0,86 e
   o retângulo sairia encolhido. O `ResizeObserver` cobre de uma vez a chegada
   da fonte, o `resize` e qualquer troca de rótulo, sem gatilho próprio. */
const acoes = component.querySelector('.submenu__acoes');

if (acoes && typeof ResizeObserver === 'function') {
    new ResizeObserver(() => {
        component.style.setProperty('--cta-medido', `${acoes.offsetWidth}px`);
    }).observe(acoes);
}

if (nav && rail) {
    const antes = nav.querySelector('.submenu__seta--antes');
    const depois = nav.querySelector('.submenu__seta--depois');

    const atualizar = () => {
        // Sem rolagem possível (desktop), os dois lados ficam falsos e nem o
        // esmaecimento nem as setas chegam a existir.
        const rolavel = rail.scrollWidth - rail.clientWidth > FOLGA;

        nav.dataset.antes = String(rolavel && rail.scrollLeft > FOLGA);
        nav.dataset.depois = String(
            rolavel && rail.scrollLeft < rail.scrollWidth - rail.clientWidth - FOLGA
        );
    };

    const avancar = (sinal) => {
        rail.scrollBy({ left: sinal * rail.clientWidth * AVANCO, behavior: 'smooth' });
    };

    if (antes) antes.addEventListener('click', () => avancar(-1));
    if (depois) depois.addEventListener('click', () => avancar(1));

    rail.addEventListener('scroll', atualizar, { passive: true });
    window.addEventListener('resize', atualizar, { passive: true });

    /* ⚠️ E quando o TRILHO muda de tamanho sem a janela mudar (09/09). Desde
       hoje a barra presa do desktop dá ao trilho um teto que abre espaço para o
       CTA (ver `.submenu--fixo` em _submenu.less): ele vai de 1240 a 1083 em
       1440 no instante em que a barra prende. Nenhum dos gatilhos acima dispara
       nisso — não há rolagem do trilho, não há `resize` e o `aria-current` pode
       nem ter mudado —, então o esmaecimento e as setas nasciam desligados
       justamente no estado em que passam a ser necessários. */
    if (typeof ResizeObserver === 'function') {
        new ResizeObserver(atualizar).observe(rail);
    }

    // A aba atual pode mudar por clique ou pela rolagem da página; quando muda,
    // ela precisa estar visível. Quem escreve o `aria-current` é o _produtos.js.
    //
    // ⚠️ SÓ O TRILHO ROLA, NUNCA A PÁGINA (04/09, ADR 0034). Isto era um
    // `scrollIntoView({ block: 'nearest', inline: 'nearest' })` na aba — e
    // `scrollIntoView` rola TODOS os ancestrais roláveis, a janela inclusive.
    // Com a barra presa no alto a aba já está à vista, mas o navegador ainda
    // ajustava a rolagem da página no instante em que o scroll-spy trocava a
    // aba: entre 769 e 1024 a página andava 60–1500px para TRÁS a cada troca de
    // seção (medido por CDP em 1024: `scrollTo(6500)` parava em 5926). Aqui a
    // conta é feita à mão, só no eixo X do trilho, com a mesma folga do
    // `scroll-margin-inline` do CSS.
    const trazerAVista = (aba) => {
        const caixa = rail.getBoundingClientRect();
        const alvo = aba.getBoundingClientRect();
        const folga = parseFloat(getComputedStyle(aba).scrollMarginLeft) || 0;
        const ini = alvo.left - caixa.left + rail.scrollLeft;
        const fim = ini + alvo.width;

        let left = null;

        if (ini - folga < rail.scrollLeft) left = ini - folga;
        else if (fim + folga > rail.scrollLeft + rail.clientWidth) left = fim + folga - rail.clientWidth;

        if (left !== null) rail.scrollTo({ left, behavior: 'smooth' });
    };

    const observador = new MutationObserver(() => {
        const atual = rail.querySelector('[aria-current="true"]');

        /* Com o SELECT ligado (ver adiante; abaixo de 1025) o trilho é uma
           lista em pé e a aba atual nem está nela: não há o que trazer à vista. */
        if (atual && !component.classList.contains('submenu--seletor') && rail.scrollWidth - rail.clientWidth > FOLGA) {
            trazerAVista(atual);
        }

        atualizar();
    });

    observador.observe(rail, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-current'],
    });

    atualizar();
}

/* ═══════════════════════════════════════════════════════ a barra presa ═══ */

/* A barra é `sticky` e para em `--panel-topo`. "Presa" é exatamente o momento
   em que o topo dela chega nesse valor — e o valor sai de `getComputedStyle`, que
   resolve o `top` de um elemento posicionado em px. Assim a régua nunca fica
   fora de sincronia com o CSS, mesmo trocando de faixa.

   São DUAS marcas, escritas juntas porque são o mesmo instante:

     · `submenu--fixo` na barra — é o que traz o monograma e o CTA;
     · `js-produtos-preso` no <html> — o aviso para o resto da página de que o
       painel de Produtos tomou a tela. O header e o hero se escondem por ele
       (ver _header.less e _hero.less): presa a barra, os dois estão cobertos, e
       o que aparecia deles eram sobras nas goteiras do painel.

   A segunda mora no <html> porque quem precisa dela são componentes IRMÃOS
   deste — não há ancestral comum mais perto, e é o mesmo lugar em que o
   boilerplate já põe `js-anim`.

   Mais uma marca e uma medida, das mesmas duas leituras — ver adiante:

     · `js-header-volta` no <html>, quando a barra já subiu 200 além da régua
       (ADR 0034; em toda largura desde o ADR 0077) OU, com ela presa e abaixo
       do degrau, quando o dedo rola PARA CIMA (ADR 0044) — é o que traz o
       header de volta;
     · `js-produtos-saindo` no <html>, quando a barra LARGA a régua e o painel
       começa a ir embora: é o que faz o gradiente do topo esmaecer;
     · `--barra-solta`, o quanto ela já largou, que mantém o gradiente da tampa
       ancorado na janela enquanto ela ainda está à vista.

   O agendamento é o de `Asa.aoRolar` (um `requestAnimationFrame` por rajada,
   compartilhado com header, hero e produtos), e as classes só são tocadas
   quando o estado muda de verdade. */
{
    let preso = null;
    let solta = null;
    let saindo = null;
    let volta = null;

    /* Quanto a barra tem de subir além da régua para o header VOLTAR (pedido de
       04/09: "depois de produtos começar a sair + 200px"; só abaixo do degrau
       até 09/09, em toda largura desde o ADR 0077).
       `js-produtos-saindo` continua entrando no primeiro pixel — é ela que
       esmaece o dourado do fundo; o header espera estes 200. */
    const VOLTA_ATRASO = 200;

    /* O GESTO (05/09, ADR 0044). Dentro da pilha, com a barra presa, rolar para
       cima traz o header de volta; rolar para baixo o esconde de novo. Abaixo
       do degrau o header é a única navegação, e até aqui ele só voltava no fim
       da pilha — a conta era "onde a barra está", nunca "para onde o dedo foi".

       `VOLTA_GESTO` é a histerese, nos dois sentidos: quantos px acumulados
       numa direção mudam o estado. Sem ela um tremor de subpixel na troca de
       direção ligaria e desligaria a classe. O acumulado zera ao inverter. */
    const VOLTA_GESTO = 24;

    let yAnterior = window.scrollY;
    let acumulado = 0;      /* px na direção atual: negativo subindo, positivo descendo */
    let porGesto = false;

    const conferir = (y = window.scrollY) => {
        const regua = parseFloat(getComputedStyle(component).top) || 0;

        /* A BARRA EXISTE NESTA LARGURA? (10/09, ADR 0088) Em "Sobre o ASA" ela
           só é desenhada abaixo de 1025 — acima o pai a põe em `display: none`,
           e um elemento sem caixa tem `getBoundingClientRect().top` igual a 0:
           "presa" na régua para sempre, e o header da página sumiria por uma
           barra que ninguém vê. Sem caixa, todos os estados abaixo são
           falsos, e a página segue como se o componente não estivesse nela.
           `getClientRects()` vazio é exatamente "não renderizado". */
        const visivel = component.getClientRects().length > 0;

        /* A posição de LAYOUT da barra, sem `translate`. Abaixo do degrau as
           PEÇAS da barra descem `--header-h` enquanto o header está à vista
           por gesto (ADR 0046 — `--pilha-desce`, em _layout.less); a `.submenu`
           em si não é transladada desde 08/09 (no iPhone o sticky
           transformado piscava), então hoje o desconto é zero. Fica por
           garantia: `getBoundingClientRect` inclui transformação, e se a barra
           voltar a ganhar uma, sem o desconto a rebaixada leria como solta,
           `porGesto` zeraria, o header se esconderia, a barra subiria, o gesto
           voltaria — um laço a cada quadro. */
        const topo = component.getBoundingClientRect().top - Asa.translateY(component);

        /* A DIREÇÃO da rolagem, acumulada até virar gesto. Só vale abaixo do
           degrau (acima o header só volta no fim da pilha — ADRs 0034 e 0077 —,
           nunca por gesto) e com a barra presa; fora disso o estado zera. */
        const dy = y - yAnterior;
        yAnterior = y;

        if (Asa.desktop.matches || !visivel || topo > regua + 1) {
            acumulado = 0;
            porGesto = false;
        } else if (dy) {
            acumulado = Math.sign(dy) === Math.sign(acumulado) ? acumulado + dy : dy;

            if (acumulado < -VOLTA_GESTO) porGesto = true;
            else if (acumulado > VOLTA_GESTO) porGesto = false;
        }

        /* QUANTO A BARRA JÁ SOLTOU DA RÉGUA. No fim da pilha ela larga o topo e
           sobe com a última seção (ver o `margin-bottom` em _submenu.less), e a
           tampa que pinta a goteira sobe com ela — o gradiente da tampa precisa
           desta medida para continuar ancorado na janela (ver o `::before` de
           lá).

           Os dois limites são o que mantém isto barato: abaixo de 0 a barra
           ainda nem prendeu, e acima da régua o alto dela já passou da borda da
           janela — não há mais goteira à vista. Fora dessa faixa de
           `--panel-topo` pixels o valor não muda, e a rolagem não escreve nada. */
        const acima = regua - topo;      /* quanto a barra já subiu além da régua */
        const agoraSolta = visivel ? Math.min(regua, Math.max(0, acima)) : 0;

        if (agoraSolta !== solta) {
            solta = agoraSolta;
            component.style.setProperty('--barra-solta', `${agoraSolta}px`);
        }

        /* `js-produtos-saindo`, no <html>: a barra largou a régua, ou seja o
           painel COMEÇOU A IR EMBORA (a última seção chegou ao topo e as duas
           sobem juntas). Quem responde é o gradiente do topo, que esmaece por
           esta marca — ver `body::before` em _base.less e a tampa em
           _submenu.less.

           A tolerância de 1 é a mesma de `preso` logo abaixo: sem ela o
           subpixel da rolagem ligaria e desligaria a classe na régua.

           ⚠️ Lida de `acima`, e NÃO de `agoraSolta` (04/09, ADR 0034): a medida
           de cima é TRAVADA em `--panel-topo`, e abaixo de 768 a régua é zero —
           a barra prende em y=0 da tela —, então `agoraSolta` ficava em zero
           para sempre e a marca nunca entrava no celular. É ela que traz o
           header de volta no fim da pilha (ver _header.less). */
        const agoraSaindo = visivel && acima > 1;

        if (agoraSaindo !== saindo) {
            saindo = agoraSaindo;
            document.documentElement.classList.toggle('js-produtos-saindo', agoraSaindo);
        }

        /* `js-header-volta`, no <html>: o painel já foi embora `VOLTA_ATRASO`
           além da régua, e o header pode descer de volta (ver o bloco "coberto
           pelo painel de Produtos" de _header.less) — ou o dedo rolou para cima
           com a barra presa (`porGesto`, só abaixo do degrau). Só quem lê é o
           header, em toda largura desde 09/09 (ADR 0077). Voltando para cima
           ela sai no mesmo ponto, e o header faz o caminho inverso. */
        const agoraVolta = visivel && (acima > VOLTA_ATRASO || porGesto);

        if (agoraVolta !== volta) {
            volta = agoraVolta;
            document.documentElement.classList.toggle('js-header-volta', agoraVolta);
        }

        const agora = visivel && topo <= regua + 1;

        if (agora === preso) return;
        preso = agora;

        component.classList.toggle('submenu--fixo', agora);
        document.documentElement.classList.toggle('js-produtos-preso', agora);
    };

    /* `Asa.aoRolar` já chama `conferir` uma vez ao registrar, com o estado
       inicial — que importa quando a página abre já rolada. O `resize` fica de
       fora dele (é rolagem, não redimensionamento) e continua próprio. */
    Asa.aoRolar(conferir);

    window.addEventListener('resize', conferir, { passive: true });

    /* O esmaecimento do header e do hero (ver _header.less) só é ARMADO depois
       do primeiro estado. Recarregar a página já rolada entra direto no estado
       preso, e com a transição no ar os dois esmaeceriam à toa — apareceriam por
       um terço de segundo justamente onde não deveriam estar.

       Sem rede de segurança de propósito: em aba de segundo plano o
       `requestAnimationFrame` não corre, a classe não entra, e o único efeito é
       não haver animação. Nada aqui esconde nem revela conteúdo. */
    requestAnimationFrame(() => {
        document.documentElement.classList.add('js-produtos-anim');
    });
}

/* ═══════════════════════════════════════════ o select do celular (ADR 0033) ═══ */

/* Abaixo de 1025 a barra vira um SELECT (Figma 10680:33332 e 10680:66279): o
   botão `.submenu__seletor` mostra a seção atual e abre o próprio trilho de
   links, em pé, como lista — ver o bloco ABAIXO DE 1025 em _submenu.less.

   Quatro coisas, todas pequenas:

     · LIGAR abaixo do degrau de desktop (`Asa.desktop`, 1025 — até 05/09 era
       `Asa.celular`, 768; ADR 0040): a classe `submenu--seletor` é o que faz
       o botão existir e o trilho só aparecer aberto. Sem ela — sem JS, ou
       acima do degrau — o trilho é a barra de abas rolável de sempre;

     · ESPELHAR a aba atual no botão: rótulo e ícone copiados do link que tem
       `aria-current`, que o scroll-spy do _produtos.js escreve. É o "conforme
       vai dando o scroll, vai atualizando" do pedido;

     · ABRIR e FECHAR: clique no botão alterna; escolher uma seção, Esc, clicar
       fora ou rolar a página fecham. A rolagem até a seção continua sendo do
       _produtos.js — aqui só se fecha a lista;

     · o estado é `aria-expanded` no botão mais `submenu--aberto` na barra. */
{
    const seletor = component.querySelector('.submenu__seletor');
    const rotulo = component.querySelector('.submenu__seletor-rotulo');
    const icone = component.querySelector('.submenu__seletor-icone');

    if (seletor && rail) {
        const CLASSES_ICONE = 'icon submenu__seletor-icone';

        const estaAberto = () => seletor.getAttribute('aria-expanded') === 'true';

        /* ── A LISTA ABERTA OCUPA A TELA, NO TOP LAYER (04/09 à noite, ADR 0038) ──
           Aberta, a barra (`.submenu__nav`) vira um POPOVER manual: `showPopover()`
           a põe no top layer — acima dos painéis seguintes (que estão em
           `--z-panel`, por cima do painel de Produtos onde ela mora) — e o CSS
           a estica pela janela inteira, rolável se as opções não couberem (ver
           `[popover]` no bloco `@maxSm` de _submenu.less). A página trava
           enquanto ela está aberta. A ENTRADA é em dois quadros: o atributo e
           o `showPopover()` mudam o `display`; a classe, no quadro seguinte,
           arma a transição (opacidade do fundo e a lista subindo). A saída
           tira a classe, espera a transição e só então esconde o popover.
           Sem a API (navegador antigo) o comportamento é o de antes: a lista
           cresce sob a barra, dentro do painel. */
        const nav = component.querySelector('.submenu__nav');
        const temPopover = Boolean(nav && typeof nav.showPopover === 'function');
        const SAIDA_LISTA = 240;
        let timerLista = 0;

        const alternar = (aberto) => {
            if (aberto === estaAberto()) return;

            seletor.setAttribute('aria-expanded', String(aberto));
            clearTimeout(timerLista);

            if (aberto) {
                if (temPopover) {
                    nav.setAttribute('popover', 'manual');
                    try { nav.showPopover(); } catch (e) { /* já aberto */ }
                    Asa.travarRolagem('submenu');
                    // Força o layout com o popover já visível antes de armar a classe.
                    void nav.offsetWidth;
                }

                if (temPopover && Asa.movimentoPermitido()) {
                    requestAnimationFrame(() => component.classList.add('submenu--aberto'));
                } else {
                    component.classList.add('submenu--aberto');
                }

                return;
            }

            component.classList.remove('submenu--aberto');

            const esconder = () => {
                if (!temPopover) return;
                try { nav.hidePopover(); } catch (e) { /* já escondido */ }
                nav.removeAttribute('popover');
                Asa.soltarRolagem('submenu');
            };

            if (temPopover && Asa.movimentoPermitido()) {
                timerLista = setTimeout(esconder, SAIDA_LISTA);
            } else {
                esconder();
            }
        };

        /* O botão mostra o que o link atual mostra: o texto, e a variante do
           ícone (`icon--money`…), que é pintado por máscara no CSS. */
        const espelhar = () => {
            const atual = rail.querySelector('.submenu__link[aria-current="true"]');

            if (!atual) return;

            if (rotulo) rotulo.textContent = atual.textContent.trim();

            const iconeAtual = atual.querySelector('.icon');
            const variante = iconeAtual
                ? Array.from(iconeAtual.classList).find((c) => c.startsWith('icon--'))
                : null;

            if (icone) icone.className = variante ? `${CLASSES_ICONE} ${variante}` : CLASSES_ICONE;
        };

        seletor.addEventListener('click', () => alternar(!estaAberto()));

        /* Escolher uma seção fecha a lista. O `preventDefault` e a rolagem são
           do handler do _produtos.js, no mesmo clique. */
        rail.addEventListener('click', (event) => {
            if (event.target.closest('.submenu__link')) alternar(false);
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && estaAberto()) {
                alternar(false);
                seletor.focus();
            }
        });

        document.addEventListener('click', (event) => {
            if (estaAberto() && !component.contains(event.target)) alternar(false);
        });

        /* A lista é um overlay preso à barra: rolar a página com ela aberta
           deixaria a lista parada sobre um conteúdo que anda. Fecha. */
        Asa.aoRolar(() => {
            if (estaAberto()) alternar(false);
        });

        /* O rótulo acompanha o scroll-spy. É um segundo observador do mesmo
           atributo — o de cima cuida do trilho rolável, este do seletor. */
        new MutationObserver(espelhar).observe(rail, {
            subtree: true,
            attributes: true,
            attributeFilter: ['aria-current'],
        });

        const ligar = (ativo) => {
            component.classList.toggle('submenu--seletor', ativo);

            if (ativo) espelhar();
            else alternar(false);
        };

        ligar(!Asa.desktop.matches);
        Asa.desktop.addEventListener('change', (event) => ligar(!event.matches));
    }
}

// A linha da aba atual nasce do centro no carregamento.
Asa.revelar(component);

});
